import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'services/api';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Badge } from 'components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { 
  HelpCircle, MessageSquare, Send, ChevronDown, ChevronUp, Clock,
  CheckCircle, AlertCircle, User, CreditCard, Settings, Shield,
  ArrowLeft, Plus, Search, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

export default function Support() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    category: 'other',
    priority: 'normal'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [faqsRes, ticketsRes] = await Promise.all([
        api.get('/support/faqs'),
        api.get('/support/tickets')
      ]);
      setFaqs(faqsRes.data.faqs);
      setTickets(ticketsRes.data.tickets);
    } catch (error) {
      console.error('Failed to fetch support data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/support/tickets', formData);
      toast.success('Support ticket submitted! We\'ll respond within 24-48 hours.');
      setDialogOpen(false);
      setFormData({ subject: '', message: '', category: 'other', priority: 'normal' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (ticketId) => {
    if (!replyMessage.trim()) return;
    
    try {
      await api.post(`/support/tickets/${ticketId}/reply`, { message: replyMessage });
      toast.success('Reply sent');
      setReplyMessage('');
      fetchData();
      // Refresh selected ticket
      const res = await api.get(`/support/tickets/${ticketId}`);
      setSelectedTicket(res.data.ticket);
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      await api.post(`/support/tickets/${ticketId}/close`, {});
      toast.success('Ticket closed');
      setSelectedTicket(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const viewTicketDetails = async (ticketId) => {
    try {
      const res = await api.get(`/support/tickets/${ticketId}`);
      setSelectedTicket(res.data.ticket);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const faqCategories = [...new Set(faqs.map(f => f.category))];

  const getCategoryIcon = (category) => {
    const icons = {
      'account': User,
      'billing': CreditCard,
      'care_hours': Clock,
      'technical': Settings,
      'verification': Shield,
      'other': HelpCircle
    };
    return icons[category] || HelpCircle;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'open': 'bg-amber-100 text-amber-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'closed': 'bg-slate-100 text-slate-500'
    };
    const labels = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'closed': 'Closed'
    };
    return <Badge className={styles[status] || styles.open}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-teal-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // Ticket detail view
  if (selectedTicket) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="support-ticket-detail">
        <div className="max-w-4xl mx-auto p-6">
          <Button variant="ghost" onClick={() => setSelectedTicket(null)} className="mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Support
          </Button>

          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-xl">{selectedTicket.subject}</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Ticket #{selectedTicket.ticket_id.slice(0, 12)} • Created {new Date(selectedTicket.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedTicket.status)}
                  {selectedTicket.status !== 'closed' && (
                    <Button variant="outline" size="sm" onClick={() => handleCloseTicket(selectedTicket.ticket_id)}>
                      Close Ticket
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Original message */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{selectedTicket.user_name}</p>
                    <p className="text-xs text-slate-500">{new Date(selectedTicket.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">{selectedTicket.message}</p>
              </div>

              {/* Responses */}
              {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-700">Responses</h4>
                  {selectedTicket.responses.map((response, idx) => (
                    <div 
                      key={response.reply_id || idx} 
                      className={`rounded-xl p-4 ${response.is_staff ? 'bg-teal-50 border border-teal-100' : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          response.is_staff ? 'bg-teal-500' : 'bg-slate-200'
                        }`}>
                          {response.is_staff ? (
                            <MessageSquare className="w-4 h-4 text-white" />
                          ) : (
                            <User className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {response.user_name}
                            {response.is_staff && <Badge className="ml-2 bg-teal-100 text-teal-700">Staff</Badge>}
                          </p>
                          <p className="text-xs text-slate-500">{new Date(response.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap">{response.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply form */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4">
                  <Label className="mb-2 block">Add a reply</Label>
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Type your message..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      className="flex-1"
                      data-testid="ticket-reply-input"
                    />
                    <Button 
                      onClick={() => handleReply(selectedTicket.ticket_id)}
                      disabled={!replyMessage.trim()}
                      data-testid="send-reply-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="support-page">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Button>
            </Link>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-500 hover:bg-teal-600" data-testid="new-ticket-btn">
                <Plus className="w-4 h-4" />
                New Support Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Contact Support</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger data-testid="ticket-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="account">Account & Login</SelectItem>
                      <SelectItem value="billing">Billing & Payments</SelectItem>
                      <SelectItem value="care_hours">Care Hours & Timesheets</SelectItem>
                      <SelectItem value="technical">Technical Issues</SelectItem>
                      <SelectItem value="verification">Verification & Background Checks</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    placeholder="Brief description of your issue"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    data-testid="ticket-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    placeholder="Please describe your issue in detail..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    rows={5}
                    data-testid="ticket-message-input"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="bg-teal-500 hover:bg-teal-600" data-testid="submit-ticket-btn">
                    {submitting ? 'Submitting...' : 'Submit Ticket'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Help & Support</h1>
          <p className="text-slate-500 mt-2">Find answers or contact our support team</p>
        </div>

        <Tabs defaultValue="faq" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
            <TabsTrigger value="faq" data-testid="faq-tab">
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQs
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tickets-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              My Tickets
              {tickets.filter(t => t.status !== 'closed').length > 0 && (
                <Badge className="ml-2 bg-teal-500">{tickets.filter(t => t.status !== 'closed').length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-6">
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="faq-search"
              />
            </div>

            {/* FAQ Categories */}
            {faqCategories.map((category) => {
              const categoryFaqs = filteredFaqs.filter(f => f.category === category);
              if (categoryFaqs.length === 0) return null;
              
              return (
                <Card key={category}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-teal-500" />
                      {category}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {categoryFaqs.map((faq) => (
                      <div 
                        key={faq.id} 
                        className="border rounded-lg overflow-hidden"
                        data-testid={`faq-${faq.id}`}
                      >
                        <button
                          onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                          className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-medium text-slate-700">{faq.question}</span>
                          {expandedFaq === faq.id ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </button>
                        {expandedFaq === faq.id && (
                          <div className="px-4 py-3 bg-slate-50 border-t">
                            <p className="text-slate-600">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}

            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No FAQs match your search</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setSearchQuery('')}
                >
                  Clear Search
                </Button>
              </div>
            )}

            {/* Contact CTA */}
            <Card className="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
              <CardContent className="py-8 text-center">
                <h3 className="text-xl font-semibold mb-2">Can't find what you're looking for?</h3>
                <p className="text-teal-100 mb-4">Our support team is here to help</p>
                <Button
                  variant="secondary"
                  onClick={() => setDialogOpen(true)}
                  data-testid="contact-support-cta"
                >
                  <MessageSquare className="w-4 h-4" />
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets" className="space-y-4">
            {tickets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No support tickets</h3>
                  <p className="text-slate-500 mb-4">You haven't submitted any support requests yet</p>
                  <Button onClick={() => setDialogOpen(true)} className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4" />
                    Create Your First Ticket
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <Card 
                    key={ticket.ticket_id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => viewTicketDetails(ticket.ticket_id)}
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-slate-900">{ticket.subject}</h4>
                            {getStatusBadge(ticket.status)}
                          </div>
                          <p className="text-sm text-slate-500 line-clamp-1">{ticket.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span>#{ticket.ticket_id.slice(0, 12)}</span>
                            <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                            <span className="capitalize">{ticket.category.replace('_', ' ')}</span>
                            {ticket.responses && ticket.responses.length > 0 && (
                              <span className="text-teal-600">{ticket.responses.length} response(s)</span>
                            )}
                          </div>
                        </div>
                        <ExternalLink className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Quick Contact Info */}
        <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <Card className="text-center">
            <CardContent className="py-6">
              <MessageSquare className="w-8 h-8 text-teal-500 mx-auto mb-3" />
              <h4 className="font-medium mb-1">Live Chat</h4>
              <p className="text-sm text-slate-500">Available Mon-Fri, 9am-5pm EST</p>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="py-6">
              <Clock className="w-8 h-8 text-teal-500 mx-auto mb-3" />
              <h4 className="font-medium mb-1">Response Time</h4>
              <p className="text-sm text-slate-500">Within 24-48 hours</p>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="py-6">
              <AlertCircle className="w-8 h-8 text-teal-500 mx-auto mb-3" />
              <h4 className="font-medium mb-1">Emergency</h4>
              <p className="text-sm text-slate-500">support@elderlink.com</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
